import styles from "./ContactsList.module.css";
import PropTypes from "prop-types";
import { useCallback, useEffect, useState } from "react";
import { ContactsContainer } from "../useContacts";
import { ContactList } from "shared/components/ContactList/ContactList";
import { FilterTabs } from "shared/components/FilterTabs/FilterTabs";
import { joinPath } from "shared/utils/joinPath";
import { makeAuthedRequest } from "shared/utils/makeAuthedRequest";
import { PREFERRED_DELIVERY } from "shared/enums";

const infoCache = new Map();

const dateKeys = new Set(["birthday", "anniversary", "dittoStartDate"]);

const preferredDeliveryLabels = {
  [PREFERRED_DELIVERY.Ground]: "ground",
  [PREFERRED_DELIVERY.Expedited]: "expedited",
  [PREFERRED_DELIVERY.AmwayPremium]: "premium delivery from amway",
  [PREFERRED_DELIVERY.HandDelivery]: "hand delivery",
};

async function fetchContactInfoCached(id) {
  if (infoCache.has(id)) return infoCache.get(id);
  const data = await makeAuthedRequest(`/customers/information/${id}`)
    .then((res) => (res.ok ? res.json() : null))
    .then((json) => json?.data || null)
    .catch(() => null);
  infoCache.set(id, data);
  return data;
}

function toSearchTokens(value, key) {
  if (value == null) return [];

  const raw = value.toString().trim();
  if (!raw) return [];

  const tokens = [raw.toLowerCase()];

  if (dateKeys.has(key)) {
    const parsed = new Date(raw);
    if (!isNaN(parsed.getTime())) {
      const longDate = new Intl.DateTimeFormat("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      })
        .format(parsed)
        .toLowerCase();

      const monthOnly = new Intl.DateTimeFormat("en-US", {
        month: "long",
      })
        .format(parsed)
        .toLowerCase();

      tokens.push(longDate, monthOnly);
    }
  }

  if (key === "preferredDeliveryMethod") {
    const label = preferredDeliveryLabels[value];
    if (label) {
      tokens.push(label);
    }
  }

  return tokens;
}

function infoMatchesSearch(info, fields, term) {
  return fields.some((key) =>
    toSearchTokens(info[key], key).some((token) => token.includes(term))
  );
}

export function ContactsList({ selectedContactId, linkBase, searchTerm }) {
  const { data, error, isLoading, filterType, setFilterType, archived } =
    ContactsContainer.useContainer();

  const [filteredContacts, setFilteredContacts] = useState([]);

  const getContactPath = useCallback((contact) => {
    const { id, isIBO } = contact;
    return joinPath(linkBase, isIBO ? "ibos" : "customers", id);
  }, []);

  useEffect(() => {
    if (!data?.contacts) return;

    infoCache.clear();

    async function filterContacts() {
      const lowerSearch = searchTerm.trim().toLowerCase();

      if (!lowerSearch) {
        setFilteredContacts(data.contacts);
        return;
      }
      const basicMatches = [];
      const contactsToSearch = [];

      // Separate basic matches and candidates to fetch info for
      for (const contact of data.contacts) {
        const mainInfo =
          (contact.name || "").toLowerCase().includes(lowerSearch) ||
          (contact.email || "").toLowerCase().includes(lowerSearch) ||
          (contact.phone || "").toLowerCase().includes(lowerSearch);

        if (mainInfo) {
          basicMatches.push(contact);
        } else if (!contact.isIBO) {
          contactsToSearch.push(contact);
        }
      }

      const toFetch = contactsToSearch.filter(
        (contact) => !infoCache.has(contact.id)
      );

      // await Promise.all(
      //   toFetch.map((contact) => fetchContactInfoCached(contact.id))
      // );

      const customerInfo = [
        "birthday",
        "occupation",
        "spouse",
        "street",
        "city",
        "state",
        "zip",
        "clientNumber",
        "anniversary",
        "dietaryPreferences",
        "dittoStartDate",
        "preferredDeliveryMethod",
        "gateCode",
        "dropOffLocation",
        "allergies",
        "healthConcerns",
      ];

      const prospectInfo = ["details"];

      const finalMatches = new Set(basicMatches.map((contact) => contact.id));

      // Check fetched info for matches
      for (const contact of contactsToSearch) {
        const info = infoCache.get(contact.id) || {};

        const customerInfoMatch = infoMatchesSearch(
          info,
          customerInfo,
          lowerSearch
        );
        const prospectInfoMatch = infoMatchesSearch(
          info,
          prospectInfo,
          lowerSearch
        );
        if (customerInfoMatch || prospectInfoMatch) {
          finalMatches.add(contact.id);
        }
      }

      const results = data.contacts.filter((contact) =>
        finalMatches.has(contact.id)
      );
      setFilteredContacts(results);
    }

    filterContacts();
  }, [data, searchTerm]);

  if (isLoading || data == null) {
    return null;
  }

  if (error) {
    console.error(error);
    return "ERROR";
  }

  return (
    <div className={styles.container}>
      {!archived && (
        <div className={styles.tabs}>
          <FilterTabs
            selected={filterType}
            onSelect={setFilterType}
            totalRecords={data ? data.count : 0}
          />
        </div>
      )}
      <div className={styles.list}>
        <ContactList
          contacts={filteredContacts}
          selectedContactId={selectedContactId}
          getContactPath={getContactPath}
        />
      </div>
    </div>
  );
}

ContactsList.propTypes = {
  /**
   * Base path for contact links.
   */
  linkBase: PropTypes.string.isRequired,
};
